/**
 * ConfigEncoder Module
 * Handles encoding and decoding of product configuration to/from URL-safe strings
 */

import type { ProductConfig, VariantConfig, ColorVariant, CustomVariant, TranslatableText } from '../types';
import { resolveText } from '../types';

// Düz string veya { tr, en?, fr?, de? } objesi
function isText(value: unknown): boolean {
  return typeof value === 'string'
    || (!!value && typeof value === 'object' && typeof (value as TranslatableText).tr === 'string');
}

// Default varyantlar (hiç varyant yoksa kullanılır)
const DEFAULT_COLOR: ColorVariant = { id: 'default', name: 'Standart', color: '#6B7280' };
const DEFAULT_CUSTOM: CustomVariant = { id: 'default', name: 'Standart' };

export class ConfigEncoder {
  /**
   * Encode ProductConfig to URL-safe base64 string
   */
  static encode(config: ProductConfig): string {
    try {
      const json = JSON.stringify(config);
      // Convert to base64
      const base64 = btoa(unescape(encodeURIComponent(json)));
      // Make URL safe: replace + with -, / with _, remove =
      return base64
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
    } catch (error) {
      throw new Error('Failed to encode product configuration');
    }
  }

  /**
   * Decode URL-safe base64 string to ProductConfig
   */
  static decode(encoded: string): ProductConfig | null {
    try {
      // Restore base64 format: replace - with +, _ with /
      let base64 = encoded
        .replace(/-/g, '+')
        .replace(/_/g, '/');

      // Add padding if needed
      while (base64.length % 4) {
        base64 += '=';
      }

      const json = decodeURIComponent(escape(atob(base64)));
      const config = JSON.parse(json) as ProductConfig;

      // Validate the decoded config
      if (!this.validate(config)) {
        return null;
      }

      return config;
    } catch (error) {
      return null;
    }
  }

  /**
   * Validate ProductConfig structure
   */
  static validate(config: unknown): config is ProductConfig {
    if (!config || typeof config !== 'object') return false;

    const c = config as ProductConfig;

    // Required fields
    if (!isText(c.name) || resolveText(c.name, 'tr').trim() === '') return false;

    // ColorVariants validation (opsiyonel ama varsa doğru formatta olmalı)
    if (c.colorVariants !== undefined) {
      if (!Array.isArray(c.colorVariants)) return false;
      for (const v of c.colorVariants) {
        if (typeof v.id !== 'string' || !isText(v.name) || typeof v.color !== 'string') {
          return false;
        }
      }
    }

    // CustomVariants validation (opsiyonel)
    if (c.customVariants !== undefined) {
      if (!Array.isArray(c.customVariants)) return false;
      for (const v of c.customVariants) {
        if (typeof v.id !== 'string' || !isText(v.name)) {
          return false;
        }
      }
    }

    // Images validation
    if (!c.images || typeof c.images !== 'object') return false;
    if (typeof c.images.basePath !== 'string') return false;
    if (typeof c.images.count !== 'number' || c.images.count < 1) return false;
    if (typeof c.images.extension !== 'string') return false;

    return true;
  }

  /**
   * Get encoded config from current URL
   */
  static getFromURL(): ProductConfig | null {
    const params = new URLSearchParams(window.location.search);
    const encoded = params.get('p');

    if (!encoded) return null;

    return this.decode(encoded);
  }

  /**
   * Generate full URL with encoded config
   */
  static generateURL(config: ProductConfig, baseURL?: string): string {
    const base = baseURL || import.meta.env.VITE_SITE_URL || window.location.origin;
    const encoded = this.encode(config);
    return `${base}/?p=${encoded}`;
  }

  /**
   * Get effective color variants (with default if empty)
   */
  static getColorVariants(config: ProductConfig): ColorVariant[] {
    return config.colorVariants && config.colorVariants.length > 0
      ? config.colorVariants
      : [DEFAULT_COLOR];
  }

  /**
   * Get effective custom variants (with default if empty)
   */
  static getCustomVariants(config: ProductConfig): CustomVariant[] {
    return config.customVariants && config.customVariants.length > 0
      ? config.customVariants
      : [DEFAULT_CUSTOM];
  }

  /**
   * Get initial color (isDefault=true veya ilk disable olmayan)
   */
  static getInitialColor(config: ProductConfig): ColorVariant {
    const colors = this.getColorVariants(config);
    return colors.find(c => c.isDefault && !c.isDisabled)
      || colors.find(c => !c.isDisabled)
      || colors[0];
  }

  /**
   * Get initial custom variant (isDefault=true veya ilk disable olmayan)
   */
  static getInitialCustom(config: ProductConfig): CustomVariant {
    const customs = this.getCustomVariants(config);
    return customs.find(c => c.isDefault && !c.isDisabled)
      || customs.find(c => !c.isDisabled)
      || customs[0];
  }

  /**
   * Convert ProductConfig to VariantConfig[] for Viewer360
   * Her renk×boyut kombinasyonu için bir VariantConfig üretir
   * Renk bazlı override'ları uygular (fotoğraflar renge bağlı)
   */
  static toViewerVariants(config: ProductConfig): VariantConfig[] {
    const imageBaseURL = import.meta.env.VITE_IMAGE_BASE_URL;
    const colors = this.getColorVariants(config);
    const customs = this.getCustomVariants(config);
    const variants: VariantConfig[] = [];
    const globalWrapAround = config.wrapAround !== false;
    const globalGalleryMode = config.galleryMode === true;

    for (const color of colors) {
      // Renk bazlı override'ları al (fotoğraflar renge bağlı)
      const override = config.variantOverrides?.[color.id];
      const count = override?.count ?? config.images.count;
      const extension = override?.extension ?? config.images.extension;
      const wrapAround = override?.wrapAround ?? globalWrapAround;
      const galleryMode = override?.galleryMode ?? globalGalleryMode;

      for (const custom of customs) {
        const id = `${color.id}_${custom.id}`;

        // basePath'in son segment'ini dosya prefix'i olarak kullan
        const filePrefix = config.images.basePath.split('/').pop() || config.images.basePath;
        variants.push({
          id,
          colorId: color.id,
          customId: custom.id,
          name: `${resolveText(color.name, 'tr')} - ${resolveText(custom.name, 'tr')}`,
          color: color.color,
          imagePath: `${imageBaseURL}/${config.images.basePath}/${color.id}`,
          frameCount: count,
          filePattern: `${filePrefix}-{n}.${extension}`,
          wrapAround: wrapAround,
          galleryMode: galleryMode
        });
      }
    }

    return variants;
  }

  /**
   * Get variant ID from color and custom IDs
   */
  static getVariantId(colorId: string, customId: string): string {
    return `${colorId}_${customId}`;
  }

  /**
   * Parse variant ID to color and custom IDs
   */
  static parseVariantId(variantId: string): { colorId: string; customId: string } {
    const [colorId, customId] = variantId.split('_');
    return { colorId: colorId || 'default', customId: customId || 'default' };
  }

  /**
   * Generate image upload instructions
   */
  static generateImageInstructions(config: ProductConfig): string {
    const imageBaseURL = import.meta.env.VITE_IMAGE_BASE_URL;
    const ext = config.images.extension;
    const colors = this.getColorVariants(config);
    const filePrefix = config.images.basePath.split('/').pop() || config.images.basePath;

    let instructions = `📁 Fotoğraf Yükleme Talimatları\n\n`;
    instructions += `Her renk için ${config.images.count} adet fotoğraf yükleyin:\n\n`;

    for (const color of colors) {
      instructions += `📂 ${resolveText(color.name, 'tr')}\n`;
      instructions += `   Konum: ${imageBaseURL}/${config.images.basePath}/${color.id}/\n`;
      instructions += `   Dosyalar: ${filePrefix}-1.${ext} ... ${filePrefix}-${config.images.count}.${ext}\n\n`;
    }

    return instructions;
  }

  /**
   * Convert string to URL-friendly slug
   */
  static slugify(text: string): string {
    return text
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Create a default/empty ProductConfig
   */
  static createDefault(): ProductConfig {
    return {
      name: '',
      description: '',
      badge: '',
      colorVariants: [],
      customVariants: [],
      images: {
        basePath: '',
        count: 24,
        extension: 'jpg'
      },
      features: []
    };
  }
}
